import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { toast } from 'react-toastify';
import Section from '../../components/Section';
import { DataContainer } from '../../components/DataContainer';
import { addPrivateNote, deletePrivateNote, fetchPrivateNotes, updatePrivateNote } from '../../store/privateNotesSlice';
import { prettifyTimestamp } from '../../tables/common';

const ACTION_TYPES = [
    'privateNotes/fetch',
    'privateNotes/add',
    'privateNotes/update',
    'privateNotes/delete',
];

const NOTE_STYLE = {
    background: '#fff8e1',
    borderLeft: '4px solid #f0b429',
    padding: '12px 16px',
    borderRadius: 4,
};

// In this app users.firstname holds the full name (lastname is unused/legacy),
// so show firstname only, like the rest of the UI does.
function authorName(note) {
    const name = note["private_note_author_firstname"];
    if(name && name.trim() !== '') return name;
    if(note["private_note_author_username"]) return note["private_note_author_username"];
    return 'Kustutatud kasutaja';
}

function PrivateNote({ queryId, note }) {
    const dispatch = useDispatch();
    const [ editing, setEditing ] = useState(false);
    const [ confirmingDelete, setConfirmingDelete ] = useState(false);
    const [ draft, setDraft ] = useState(note["private_note_content"]);

    const wasEdited = note["private_note_updated"] && note["private_note_updated"] !== note["private_note_created"];

    function startEdit() {
        setDraft(note["private_note_content"]);
        setConfirmingDelete(false);
        setEditing(true);
    }

    async function saveEdit() {
        if(draft.trim() === '') {
            toast.error("Märge ei saa olla tühi");
            return;
        }
        await dispatch(updatePrivateNote({ id: queryId, noteId: note["private_note_id"], content: draft }));
        setEditing(false);
        toast.success("Märge muudetud!");
    }

    async function confirmDelete() {
        await dispatch(deletePrivateNote({ id: queryId, noteId: note["private_note_id"] }));
        toast.success("Märge kustutatud!");
    }

    return (
        <div className="column g8" style={NOTE_STYLE}>
            <div className="line g16" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div className="column">
                    <b>{authorName(note)}</b>
                    <span style={{ color: '#888', fontSize: 14 }}>
                        {prettifyTimestamp(note["private_note_created"])}
                        {wasEdited ? ` (muudetud ${prettifyTimestamp(note["private_note_updated"])})` : ''}
                    </span>
                </div>

                {!editing && !confirmingDelete && (
                    <div className="row g8">
                        <button className="borderless" onClick={startEdit}>Muuda</button>
                        <button className="borderless" onClick={() => setConfirmingDelete(true)}>Kustuta</button>
                    </div>
                )}

                {confirmingDelete && (
                    <div className="row g8 center-cross">
                        <span style={{ fontSize: 14 }}>Kustutada jäädavalt?</span>
                        <button onClick={confirmDelete}>Jah, kustuta</button>
                        <button className="borderless" onClick={() => setConfirmingDelete(false)}>Loobu</button>
                    </div>
                )}
            </div>

            {editing ? (
                <div className="column g8">
                    <TextareaAutosize spellCheck={false} minRows={3} style={{ resize: 'none', fontFamily: 'unset' }} value={draft} onChange={(e) => setDraft(e.target.value)} />
                    <div className="row g8" style={{ justifyContent: 'flex-end' }}>
                        <button className="borderless" onClick={() => setEditing(false)}>Loobu</button>
                        <button onClick={saveEdit}>Salvesta</button>
                    </div>
                </div>
            ) : (
                <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{note["private_note_content"]}</p>
            )}
        </div>
    );
}

function PrivateNotes({ id }) {
    const dispatch = useDispatch();
    const notes = useSelector((state) => state.privateNotes[id]);
    const [ newContent, setNewContent ] = useState('');

    useEffect(() => {
        dispatch(fetchPrivateNotes({ id }));
    }, [dispatch, id]);

    async function onAdd() {
        if(newContent.trim() === '') {
            toast.error("Märge ei saa olla tühi");
            return;
        }
        await dispatch(addPrivateNote({ id, content: newContent }));
        setNewContent('');
        toast.success("Märge lisatud!");
    }

    const count = notes ? notes.length : 0;

    return (<div style={{ width: 0, minWidth: '100%' }}>
        <hr />

        <Section title={`Privaatsed märkmed (${count})`} initiallyHidden={true} titleColor="#000">
            <DataContainer actionTypes={ACTION_TYPES}>
                <div className="column g32" style={{ width: '100%' }}>
                    <p style={{ color: '#888' }}>
                        Neid märkmeid ei kuvata kliendile, juhile ega partnerile ning neid ei saadeta ühegi e-kirjaga.
                    </p>

                    <div className="column g8">
                        <h3>Lisa uus privaatne märge</h3>
                        <TextareaAutosize spellCheck={false} minRows={3} style={{ resize: 'none', fontFamily: 'unset' }} placeholder='Sisemine info, kokkulepped, tähelepanekud...' value={newContent} onChange={(e) => setNewContent(e.target.value)} />
                        <i style={{ color: '#888', fontSize: 14 }}>Vajuta Enter-it, et uuele reale minna</i>
                        <button onClick={onAdd}>Lisa märge</button>
                    </div>

                    <div className="column g16">
                        {count === 0
                            ? <p style={{ color: '#888' }}>Privaatseid märkmeid pole veel lisatud.</p>
                            : notes.map(note => <PrivateNote key={note["private_note_id"]} queryId={id} note={note} />)}
                    </div>
                </div>
            </DataContainer>
        </Section>
    </div>);
}

export default PrivateNotes;
